import React, { forwardRef } from "react";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: string;
  error?: string;
  helpText?: string;
  options: SelectOption[];
  placeholder?: string;
  variant?: "default" | "outline" | "filled";
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helpText,
      options,
      placeholder,
      variant = "default",
      size = "md",
      fullWidth = false,
      className = "",
      id,
      required,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = error ? `${selectId}-error` : undefined;
    const helpId = helpText ? `${selectId}-help` : undefined;

    const baseClasses = [
      "border transition-colors duration-200 focus:outline-none focus:ring-2",
      "appearance-none cursor-pointer",
      fullWidth ? "w-full" : "",
    ];

    const sizeClasses = {
      sm: "px-2 py-1 text-sm",
      md: "px-3 py-2 text-base",
      lg: "px-4 py-3 text-lg",
    };

    const variantClasses = {
      default: error
        ? "border-red-500 bg-zinc-800 text-white focus:border-red-400 focus:ring-red-500"
        : "border-zinc-700 bg-zinc-800 text-white focus:border-amber-400 focus:ring-amber-400",
      outline: error
        ? "border-2 border-red-500 bg-zinc-900 text-white focus:border-red-400 focus:ring-red-500"
        : "border-2 border-zinc-700 bg-zinc-900 text-white focus:border-amber-400 focus:ring-amber-400",
      filled: error
        ? "border-red-500 bg-zinc-800 text-white focus:border-red-400 focus:ring-red-500"
        : "border-zinc-600 bg-zinc-800 text-white focus:border-amber-400 focus:ring-amber-400",
    };

    const selectClasses = [
      ...baseClasses,
      sizeClasses[size],
      variantClasses[variant],
      props.disabled ? "opacity-50 cursor-not-allowed bg-zinc-800" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={fullWidth ? "w-full" : ""}>
        {label && (
          <label
            htmlFor={selectId}
            className={`block text-sm font-medium mb-1 ${error ? "text-red-400" : "text-amber-400"}`}
          >
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={selectClasses}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={[errorId, helpId].filter(Boolean).join(" ") || undefined}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Custom dropdown arrow */}
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
            <svg
              className="w-4 h-4 text-zinc-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {error && (
          <p id={errorId} className="mt-1 text-sm text-red-400" role="alert">
            {error}
          </p>
        )}

        {helpText && !error && (
          <p id={helpId} className="mt-1 text-sm text-zinc-400">
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = "Select";

export default Select;
